import * as React from 'react'
import { useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { AppService } from '@halo/services/app.service'
import { Form, Input, Select } from 'antd'
import { PageAnalysisConfigsService } from '@halo/services/page-analysis-configs.service'
import { simpleNotification } from '@halo/_utils/notifications'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { DomainService } from '@halo/services/domain.service'
import { DomainDto } from '@halo/dtos/domain.dto'
import { Drawer } from '@pushly/aqe/lib/components'
import { deepMerge } from '@halo/_utils/object'
import { IPageAnalysisConfig } from '@pushly/aqe/lib/interfaces'

export interface IEditPageAnalysisConfigDrawerProps {
    pageAnalysisConfig: Partial<IHydratedPageAnalysisConfig>
    onUpdate: (pageAnalysisConfig: IHydratedPageAnalysisConfig) => void
    onClose: () => void
}

function EditPageAnalysisConfigDrawer(props: IEditPageAnalysisConfigDrawerProps) {
    const appService = Container.get(AppService)
    const [isSaving, setIsSaving] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [domains, setDomains] = useState<DomainDto[]>()
    const [pageAnalysisConfigs, setPageAnalysisConfigs] = useState<IPageAnalysisConfig[]>()

    const [form] = Form.useForm()

    const pageAnalysisConfigService: PageAnalysisConfigsService = Container.get(PageAnalysisConfigsService)
    const domainsService: DomainService = Container.get(DomainService)

    useEffect(() => {
        const setDependencies = async () => {
            setIsLoading(true)

            // Fetch dependencies first so we know the Default config id before
            // initializing the form — that lets us pre-select Default as the
            // parent when creating a new (non-default) PAC.
            const [d, pacs] = await Promise.all([fetchDomains(), fetchPageAnalysisConfigs()])
            setDomains(d)
            setPageAnalysisConfigs(pacs)

            const isCreating = !props.pageAnalysisConfig?.id
            const defaultParentId =
                isCreating && !props.pageAnalysisConfig?.is_default
                    ? pacs?.find((p) => p.is_default)?.id
                    : undefined

            // The Select for "Associated Domains" is keyed by domain id, but the
            // hydrated config carries them as { id, name } objects — feeding that
            // shape into a Select whose options are numeric ids leaves antd unable
            // to match anything, so it renders blank "empty token" tags. Normalize
            // to ids on the way into the form; save() reshapes back to objects.
            const initialValues = {
                ...props.pageAnalysisConfig,
                parent_id: props.pageAnalysisConfig?.parent_id ?? defaultParentId,
                associated_domains: (props.pageAnalysisConfig?.associated_domains || []).map(
                    (ad) => ad.id,
                ),
            }
            form.resetFields()
            form.setFieldsValue(initialValues)

            setIsLoading(false)
        }

        setDependencies()
    }, [props.pageAnalysisConfig])

    async function fetchDomains() {
        const domainFields = ['id', 'name', 'display_name']

        const res = await domainsService.fetchAll({
            query: {
                pagination: false,
                fields: domainFields.join(','),
            },
        })

        return res.data
    }

    async function fetchPageAnalysisConfigs() {
        const res = await pageAnalysisConfigService.fetchAll({
            query: { pagination: 0 },
        })
        return res.data
    }

    async function save() {
        setIsSaving(true)
        const formValues = form.getFieldsValue()
        // Re-shape the id-only Select value back to the {id, name} objects the
        // API expects (mirror of the normalize step in the load effect above).
        const associatedDomains = ((formValues.associated_domains as unknown as number[]) || []).map(
            (id) => {
                const domain = domains?.find((d) => d.id === id)
                return { id, name: domain?.name ?? '' }
            },
        )
        const saveDto = deepMerge(props.pageAnalysisConfig, {
            ...formValues,
            associated_domains: associatedDomains,
        })

        try {
            let successMessage = ''
            let pacId = props.pageAnalysisConfig.id

            if (props.pageAnalysisConfig.id) {
                await pageAnalysisConfigService.patch(props.pageAnalysisConfig.id, saveDto)
                successMessage = 'Page Analysis Config Updated'
            } else {
                const nres = await pageAnalysisConfigService.create(saveDto)
                pacId = nres.data.id
                successMessage = 'Page Analysis Config Created'
            }

            // Fetch a new hydrated pac to get all extended pac fields
            const hres = await pageAnalysisConfigService.fetchById(pacId, {
                query: {
                    hydrated: true,
                },
            })
            simpleNotification('success', successMessage)
            props.onUpdate(hres.data as IHydratedPageAnalysisConfig)
        } catch (e) {
            simpleNotification('error', e.toString())
        }
        setIsSaving(false)
    }

    function render() {
        return (
            <Drawer
                width={800}
                getContainer={appService.getAppContainer}
                className="edit-category-drawer"
                title={
                    props.pageAnalysisConfig && props.pageAnalysisConfig.id
                        ? 'Edit Page Analysis Config'
                        : 'Add Page Analysis Config'
                }
                placement="right"
                closable={!isSaving}
                submitText={
                    props.pageAnalysisConfig && props.pageAnalysisConfig.id
                        ? 'Update Page Analysis Config'
                        : 'Add Page Analysis Config'
                }
                disableSubmit={isSaving}
                onSubmit={async () => {
                    form.submit()
                }}
                onClose={props.onClose}
                open={!!props.pageAnalysisConfig}
            >
                {props.pageAnalysisConfig && (
                    <Form
                        form={form}
                        layout="horizontal"
                        size="small"
                        labelAlign="left"
                        requiredMark={false}
                        onFinish={() => {
                            save()
                        }}
                    >
                        <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
                            <Input autoComplete="off" />
                        </Form.Item>

                        {!props.pageAnalysisConfig.is_default && (
                            <Form.Item label="Extends" name="parent_id">
                                <Select
                                    allowClear={true}
                                    showSearch={true}
                                    optionFilterProp="children"
                                    filterOption={(input, option) => {
                                        return (
                                            option.props.children
                                                .toString()
                                                .toLowerCase()
                                                .indexOf(input.toLowerCase()) >= 0
                                        )
                                    }}
                                >
                                    {pageAnalysisConfigs?.map((d) => {
                                        if (d.id !== props.pageAnalysisConfig.id) {
                                            return (
                                                <Select.Option key={d.id} value={d.id}>
                                                    {d.name}
                                                </Select.Option>
                                            )
                                        }
                                    })}
                                </Select>
                            </Form.Item>
                        )}

                        <Form.Item
                            label="Associated Domains"
                            name="associated_domains"
                            extra={
                                <span>
                                    Domains can only have one explicit page analysis config association at a time.
                                    Removing a domain page analysis config association here will cause the domain to
                                    fallback to the Default page analysis config. Adding a domain page analysis config
                                    association here will overwrite any preexisting explicit association that domain
                                    may have.
                                </span>
                            }
                        >
                            <Select
                                mode="multiple"
                                filterOption={(input, option) => {
                                    return (
                                        option.props.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    )
                                }}
                            >
                                {domains?.map((d) => {
                                    return (
                                        <Select.Option key={d.id} value={d.id}>
                                            {d.display_name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Form>
                )}
            </Drawer>
        )
    }

    return render()
}

export default EditPageAnalysisConfigDrawer
